import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './PageLoader.css';

const PageLoaderContext = createContext({
  isLoading: false,
  setPageLoading: () => { },
  showLoader: () => { },
  hideLoader: () => { },
});

export const usePageLoader = () => useContext(PageLoaderContext);

// Routes that must never display the global page loader
const isLoaderExempt = (path) => {
  return (
    path === '/home' ||
    path === '/' ||
    path === '/signin' ||
    path === '/signup' ||
    path === '/forgot-password' ||
    path === '/assets-list' ||
    path === '/analytics'
  );
};

export const PageLoaderProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  // Whenever navigating to exempt routes, immediately force loader to false
  useEffect(() => {
    if (isLoaderExempt(currentPath)) {
      setIsLoading(false);
    }
  }, [currentPath]);

  const setPageLoading = useCallback(
    (loading) => {
      // Never allow loader on exempt routes
      if (isLoaderExempt(location.pathname)) {
        setIsLoading(false);
        return;
      }
      setIsLoading(Boolean(loading));
    },
    [location.pathname]
  );

  const showLoader = useCallback(() => {
    if (!isLoaderExempt(location.pathname)) {
      setIsLoading(true);
    }
  }, [location.pathname]);

  const hideLoader = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <PageLoaderContext.Provider value={{ isLoading, setPageLoading, showLoader, hideLoader }}>
      {children}
      <AnimatePresence>
        {isLoading && !isLoaderExempt(location.pathname) && (
          <motion.div
            key="page-loader-overlay"
            className="page-loader-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
          >
            <div className="page-loader-backdrop-glow" />

            <div className="page-loader-content">
              {/* Futuristic Cyber Spinner */}
              <div className="page-loader-spinner-container">
                <div className="cyber-ring ring-outer" />
                <div className="cyber-ring ring-middle" />
                <div className="cyber-ring ring-inner" />
                <div className="cyber-center-orb" />
              </div>

              {/* Status Text */}
              <p className="page-loader-title">Loading... Please Wait...</p>

              {/* Progress Scan Bar */}
              <div className="page-loader-progress-wrap">
                <div className="page-loader-progress-bar" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageLoaderContext.Provider>
  );
};

export default PageLoaderProvider;
